"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.appMountPoint = exports.AppCompositionOperationError = void 0;
exports.shellWithoutRemovedFrame = shellWithoutRemovedFrame;
exports.applyAppCompositionOperations = applyAppCompositionOperations;
const app_base_contract_1 = require("./app-base-contract");
/**
 * An operation the composition of an App Container refuses. A refused mount
 * point carries a code and the place; the other kinds carry neither.
 */
class AppCompositionOperationError extends Error {
    constructor(kind, message, code, mountPoint) {
        super(message);
        this.kind = kind;
        this.code = code;
        this.mountPoint = mountPoint;
        this.name = 'AppCompositionOperationError';
    }
}
exports.AppCompositionOperationError = AppCompositionOperationError;
/** The place of a mount, as the pages of the App Container name it. */
const appMountPoint = (mount) => (0, app_base_contract_1.joinMountedAppPath)(mount.routePrefix, '/');
exports.appMountPoint = appMountPoint;
function mountedAt(mount, mountPoint) {
    return {
        ...mount,
        routePrefix: mountPoint,
        ...(mount.entryRoutePath ? { entryRoutePath: mountPoint } : {}),
    };
}
/**
 * The shell of an App Container for these mounts: a chosen frame mount that is
 * not among them falls back to the automatic frame choice.
 */
function shellWithoutRemovedFrame(shell, mounts) {
    if (typeof shell.frameMountId !== 'string' ||
        mounts.some(mount => mount.mountId === shell.frameMountId)) {
        return shell;
    }
    const { frameMountId: _removed, ...rest } = shell;
    return rest;
}
const mountNotFound = (mountId) => new AppCompositionOperationError('mountNotFound', `App mount ${mountId} not found`);
/**
 * The composition of an App Container after the operations, each applied to
 * what the one before left. Reads nothing but its arguments and changes
 * neither of them. Throws AppCompositionOperationError for an operation the
 * composition refuses, and the ZodError of AppCompositionSchema for a result
 * that is no valid composition.
 */
function applyAppCompositionOperations(current, operations) {
    let composition = app_base_contract_1.AppCompositionSchema.parse(current);
    for (const operation of operations) {
        switch (operation.type) {
            case 'mountApp': {
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    mounts: [...composition.mounts, operation.mount],
                });
                break;
            }
            case 'unmountApp': {
                const mounts = composition.mounts.filter(mount => mount.mountId !== operation.mountId);
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    shell: shellWithoutRemovedFrame(composition.shell, mounts),
                    mounts,
                });
                break;
            }
            case 'unmountAppEverywhere': {
                const mounts = composition.mounts.filter(mount => mount.appId !== operation.appId);
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    shell: shellWithoutRemovedFrame(composition.shell, mounts),
                    mounts,
                });
                break;
            }
            case 'setFrameMount': {
                const { frameMountId: _chosen, ...shell } = composition.shell;
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    shell: operation.mountId === undefined
                        ? shell
                        : { ...shell, frameMountId: operation.mountId },
                });
                break;
            }
            case 'updateMount': {
                let found = false;
                const mounts = composition.mounts.map(mount => {
                    if (mount.mountId !== operation.mountId)
                        return mount;
                    found = true;
                    const { navigationLabel, ...patch } = operation.patch;
                    const { navigationLabel: ownLabel, ...unnamed } = mount;
                    // null drops the own name: the App shows under its own again.
                    const label = navigationLabel === null
                        ? undefined
                        : (navigationLabel ?? ownLabel);
                    return {
                        ...unnamed,
                        ...patch,
                        ...(label ? { navigationLabel: label } : {}),
                    };
                });
                if (!found)
                    throw mountNotFound(operation.mountId);
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    mounts,
                });
                break;
            }
            case 'moveMount': {
                const issue = (0, app_base_contract_1.appRoutePathIssue)(operation.routePrefix, 'mount');
                if (issue) {
                    throw new AppCompositionOperationError('mountPointInvalid', issue, 'APP_BASE_MOUNT_POINT_INVALID', operation.routePrefix);
                }
                const target = (0, app_base_contract_1.joinMountedAppPath)(operation.routePrefix, '/');
                const moved = composition.mounts.find(mount => mount.mountId === operation.mountId);
                if (!moved)
                    throw mountNotFound(operation.mountId);
                const occupant = composition.mounts.find(mount => mount !== moved && (0, exports.appMountPoint)(mount) === target);
                if (occupant && !operation.swap) {
                    throw new AppCompositionOperationError('mountPointTaken', `Another App Module is mounted at ${target}`, 'APP_BASE_MOUNT_POINT_TAKEN', target);
                }
                const origin = (0, exports.appMountPoint)(moved);
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    mounts: composition.mounts.map(mount => {
                        if (mount === moved)
                            return mountedAt(mount, target);
                        if (mount === occupant)
                            return mountedAt(mount, origin);
                        return mount;
                    }),
                });
                break;
            }
            case 'updateShell': {
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    shell: {
                        ...composition.shell,
                        ...operation.patch,
                    },
                });
                break;
            }
            case 'reorderMounts': {
                const expected = new Set(composition.mounts.map(mount => mount.mountId));
                const requested = new Set(operation.mountIds);
                if (expected.size !== requested.size ||
                    [...expected].some(mountId => !requested.has(mountId))) {
                    throw new AppCompositionOperationError('reorderIncomplete', 'App reorderMounts must include every existing mount exactly once');
                }
                const indexByMountId = new Map(operation.mountIds.map((mountId, index) => [mountId, index]));
                const mounts = [...composition.mounts]
                    .sort((a, b) => (indexByMountId.get(a.mountId) ?? 0) -
                    (indexByMountId.get(b.mountId) ?? 0))
                    .map((mount, index) => ({ ...mount, order: index }));
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    mounts,
                });
                break;
            }
            case 'setNavigationPlacement': {
                let found = false;
                const mounts = composition.mounts.map(mount => {
                    if (mount.mountId !== operation.mountId)
                        return mount;
                    found = true;
                    return {
                        ...mount,
                        navigation: operation.navigation,
                        ...(operation.navigationPlacement
                            ? { navigationPlacement: operation.navigationPlacement }
                            : {}),
                        ...(operation.navigationLabel
                            ? { navigationLabel: operation.navigationLabel }
                            : {}),
                    };
                });
                if (!found)
                    throw mountNotFound(operation.mountId);
                composition = app_base_contract_1.AppCompositionSchema.parse({
                    ...composition,
                    mounts,
                });
                break;
            }
        }
    }
    return composition;
}
